// Delete File Tool - Provides file and directory deletion functions for workspace
// Extends WorkspaceTool - has access to appType and toolSystem

import { WorkspaceTool } from "../../base/WorkspaceTool.js";
import { logger } from "../../../../../../core/logger.js";

export class DeleteFileTool extends WorkspaceTool {
  constructor() {
    super();
    this.toolName = 'deleteFileTool';
  }
  /**
   * Delete a file or directory
   * @param {string} path - Path to the file or directory to delete
   * @param {boolean} [recursive=false] - Delete directories recursively (required for non-empty directories)
   * @returns {Promise<Object>} Result with operation status
   */
  async deleteFile(path, recursive = false) {
    try {
      // Validate path
      if (!path || typeof path !== "string") {
        return this.returnError({}, "Valid file or directory path required");
      }

      // Route based on app type
      if (this.appType === "browser") {
        return await this.deleteFileForBrowser(path, recursive);
      }
      if (this.appType === "local") {
        return await this.deleteFileForLocal(path, recursive);
      }
      return this.returnError(
        {},
        `App type '${this.appType}' not yet implemented for deleteFile`
      );
    } catch (error) {
      logger.error(
        {
          tags: "tools|deletefile|error",
          color1: "red",
        },
        `Failed to delete: ${path}`,
        error
      );
      return this.returnError({}, error.message);
    }
  }

  /**
   * Delete file or directory for browser apps using Lightning FS
   */
  async deleteFileForBrowser(path, recursive) {
    const fs = this.fs?.promises;
    if (!fs) {
      return this.returnError(
        {},
        "Lightning FS not available - browser app filesystem not initialized"
      );
    }

    // Normalize path - Lightning FS uses absolute paths
    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    // Prepend /files/ to the filesystem path
    const filesystemPath = `/files${normalizedPath}`;

    try {
      // Check if path exists
      const stats = await fs.stat(filesystemPath);
      const isDir =
        typeof stats.isDirectory === "function"
          ? stats.isDirectory()
          : stats.type === "dir";

      if (isDir) {
        // Delete directory
        return await this.deleteDirectory(
          filesystemPath,
          normalizedPath,
          recursive
        );
      } else {
        // Delete file
        await fs.unlink(filesystemPath);

        // Sync changes with workspace manager to update UI
        if (this.workspaceManager?.syncOpenFiles) {
          await this.workspaceManager.syncOpenFiles([normalizedPath]);
        }

        logger.log(
          {
            tags: "tools|deletefile|info",
            color1: "lime",
          },
          `Deleted file: ${filesystemPath}`,
          {
            path: normalizedPath,
          }
        );

        return this.returnSuccess(
          {
            path: normalizedPath,
            type: "file",
          },
          "File deleted successfully"
        );
      }
    } catch (error) {
      if (error.code === "ENOENT") {
        return this.returnError({}, `File or directory not found: ${path}`);
      }
      if (error.code === "ENOTEMPTY" || error.code === "EEXIST") {
        return this.returnError(
          {},
          `Directory is not empty. Use recursive=true to delete non-empty directories: ${path}`
        );
      }
      throw error;
    }
  }

  /**
   * Delete file or directory for local apps (Revolt Desktop + workspaceLocalAppAPI).
   */
  async deleteFileForLocal(path, recursive) {
    const api = this.getWorkspaceLocalAppApi();
    if (!api?.deleteFile) {
      return this.returnError(
        {},
        "Local deletes require Revolt Desktop (workspaceLocalAppAPI.deleteFile)."
      );
    }

    const root = this.getLocalWorkingDirectoryRoot();
    if (!root) {
      return this.returnError(
        {},
        "Local app has no working directory (settings.workingDirectoryPath)."
      );
    }

    const normalizedPath = this.normalizeAndValidatePath(path);
    if (!normalizedPath) {
      return this.returnError({}, "Invalid path");
    }
    const relativePath = normalizedPath.replace(/^\/+/, "");
    if (!relativePath) {
      return this.returnError({}, "Cannot delete workspace root");
    }

    const res = await api.deleteFile({
      rootPath: root,
      relativePath,
      recursive,
    });

    if (!res?.success) {
      return this.returnError({}, res?.error || "Failed to delete on disk");
    }

    if (this.workspaceManager?.syncOpenFiles) {
      await this.workspaceManager.syncOpenFiles([normalizedPath]);
    }

    const type = res.type === "directory" ? "directory" : "file";
    logger.log(
      {
        tags: "tools|deletefile|info",
        color1: "lime",
      },
      `Deleted ${type} (local): ${normalizedPath}`,
      { path: normalizedPath, recursive: !!recursive }
    );

    return this.returnSuccess(
      {
        path: normalizedPath,
        type,
        recursive: type === "directory" ? !!recursive : undefined,
      },
      type === "directory"
        ? "Directory deleted successfully"
        : "File deleted successfully"
    );
  }

  /**
   * Delete a directory, optionally recursively
   * @param {string} filesystemPath - Full filesystem path (with /files/ prefix)
   * @param {string} normalizedPath - Normalized path (without /files/ prefix)
   * @param {boolean} recursive - Whether to delete recursively
   * @returns {Promise<Object>} Result with operation status
   */
  async deleteDirectory(filesystemPath, normalizedPath, recursive) {
    const fs = this.fs?.promises;

    if (recursive) {
      // Delete all contents first, then the directory
      await this.deleteDirectoryContents(filesystemPath, fs);
    }

    // Delete the directory itself
    try {
      await fs.rmdir(filesystemPath);

      // Sync changes with workspace manager to update UI
      if (this.workspaceManager?.syncOpenFiles) {
        await this.workspaceManager.syncOpenFiles([normalizedPath]);
      }
    } catch (error) {
      if (error.code === "ENOTEMPTY") {
        return this.returnError(
          {},
          `Directory is not empty. Use recursive=true to delete non-empty directories: ${normalizedPath}`
        );
      }
      throw error;
    }

    logger.log(
      {
        tags: "tools|deletefile|info",
        color1: "lime",
      },
      `Deleted directory: ${filesystemPath}`,
      {
        path: normalizedPath,
        recursive,
      }
    );

    return this.returnSuccess(
      {
        path: normalizedPath,
        type: "directory",
        recursive,
      },
      "Directory deleted successfully"
    );
  }

  /**
   * Recursively delete all contents of a directory
   * @param {string} dirPath - Directory path to empty
   * @param {Object} fs - Filesystem promises interface
   */
  async deleteDirectoryContents(dirPath, fs) {
    try {
      const items = await fs.readdir(dirPath);

      for (const item of items) {
        const itemPath =
          dirPath === "/files" ? `/files/${item}` : `${dirPath}/${item}`;

        try {
          const stats = await fs.stat(itemPath);
          const isDir =
            typeof stats.isDirectory === "function"
              ? stats.isDirectory()
              : stats.type === "dir";

          if (isDir) {
            // Recursively delete subdirectory contents first
            await this.deleteDirectoryContents(itemPath, fs);
            // Then delete the subdirectory
            await fs.rmdir(itemPath);
          } else {
            // Delete file
            await fs.unlink(itemPath);
          }
        } catch (error) {
          logger.warn(
            {
              tags: "tools|deletefile|entry",
              color1: "yellow",
            },
            `Failed to delete entry: ${itemPath}`,
            error
          );
          // Continue with other entries even if one fails
        }
      }
    } catch (error) {
      logger.error(
        {
          tags: "tools|deletefile|read",
          color1: "red",
        },
        `Failed to read directory during deletion: ${dirPath}`,
        error
      );
      throw error;
    }
  }
}
